import type { Prisma } from "@prisma/client";
import { createServerFn } from "@tanstack/react-start";
import { Effect, pipe } from "effect";

import { getPrisma } from "@/server-fns/only/prisma";
import { getServerSupabase } from "@/server-fns/only/supabase";

export type StoreWithBookingSettings = Prisma.StoreGetPayload<{
    include: { bookingSettings: true };
}>;

export const fetchStoreByUserId = async (userId: string) => {
    const prisma = getPrisma();
    return prisma.store.findFirst({
        where: { ownerId: userId },
        include: { bookingSettings: true },
    });
};

export const fetchAllStoresByUserId = async (userId: string) => {
    const prisma = getPrisma();
    return prisma.store.findMany({
        where: { ownerId: userId },
        include: { bookingSettings: true },
        orderBy: { createdAt: "asc" },
    });
};

export const getCurrentStoreStatusFn = createServerFn({ method: "GET" }).handler(() => {
    const getStatus = Effect.gen(function* () {
        const supabase = yield* Effect.tryPromise({
            try: () => getServerSupabase(),
            catch: (error) => new Error(`Failed to initialize Supabase: ${error}`),
        });

        const {
            data: { user },
        } = yield* Effect.tryPromise({
            try: () => supabase.auth.getUser(),
            catch: (error) => new Error(`Failed to get user: ${error}`),
        });

        if (!user || user.is_anonymous) {
            return { status: "unauthenticated" as const };
        }

        const store = yield* Effect.tryPromise({
            try: () => fetchStoreByUserId(user.id),
            catch: (error) => new Error(`Failed to fetch store: ${error}`),
        });

        if (!store) {
            return { status: "no-store" as const };
        }

        if (!store.onboardingCompletedAt) {
            return { status: "in-progress" as const, store };
        }

        return { status: "ok" as const, store };
    });

    return pipe(
        getStatus,
        Effect.catchAll((error) => {
            console.error("[getCurrentStoreStatusFn]", error.message);
            return Effect.succeed({ status: "unauthenticated" as const });
        }),
        Effect.runPromise,
    );
});

export const getStoresByUserFn = createServerFn({ method: "GET" }).handler(() => {
    const getStores = Effect.gen(function* () {
        const supabase = yield* Effect.tryPromise({
            try: () => getServerSupabase(),
            catch: (error) => new Error(`Failed to initialize Supabase: ${error}`),
        });

        const {
            data: { user },
        } = yield* Effect.tryPromise({
            try: () => supabase.auth.getUser(),
            catch: (error) => new Error(`Failed to get user: ${error}`),
        });

        if (!user || user.is_anonymous) {
            return { status: "unauthenticated" as const };
        }

        const stores = yield* Effect.tryPromise({
            try: () => fetchAllStoresByUserId(user.id),
            catch: (error) => new Error(`Failed to fetch stores: ${error}`),
        });

        return { status: "ok" as const, stores };
    });

    return pipe(
        getStores,
        Effect.catchAll((error) => {
            console.error("[getStoresByUserFn]", error.message);
            return Effect.succeed({ status: "error" as const });
        }),
        Effect.runPromise,
    );
});

export const getStoreByIdFn = createServerFn({ method: "GET" })
    .inputValidator((data: { storeId: string }) => data)
    .handler(async ({ data }) => {
        const getStore = Effect.gen(function* () {
            const supabase = yield* Effect.tryPromise({
                try: () => getServerSupabase(),
                catch: (error) => new Error(`Failed to initialize Supabase: ${error}`),
            });

            const {
                data: { user },
            } = yield* Effect.tryPromise({
                try: () => supabase.auth.getUser(),
                catch: (error) => new Error(`Failed to get user: ${error}`),
            });

            if (!user || user.is_anonymous) {
                return { status: "unauthenticated" as const, store: null };
            }

            const prisma = getPrisma();
            const store = yield* Effect.tryPromise({
                try: () =>
                    prisma.store.findFirst({
                        where: { id: data.storeId, ownerId: user.id },
                        include: { bookingSettings: true },
                    }),
                catch: (error) => new Error(`Failed to fetch store: ${error}`),
            });

            if (!store) {
                return { status: "not-found" as const, store: null };
            }

            return { status: "ok" as const, store };
        });

        return pipe(
            getStore,
            Effect.catchAll((error) => {
                console.error("[getStoreByIdFn]", error.message);
                return Effect.succeed({ status: "unauthenticated" as const, store: null });
            }),
            Effect.runPromise,
        );
    });
